import { Injectable, inject } from '@angular/core';
import { getDocs } from 'firebase/firestore';

import { ownedCollection } from '../../utils/firestore-ownership';
import { CardItemModel, CardModel, CardStatementModel, CategoryModel, EntryModel, Month } from './finance.model';
import { FinanceStoreService } from './finance-store.service';
import { monthOf } from '../shared/month/month.service';

export interface ComputedLine {
  description: string,
  category: string,
  value: number
}

export interface CardStatementSummary {
  card: CardModel,
  items: CardItemModel[],
  total: number
}

export interface MonthSummary {
  incomes: EntryModel[],
  dividends: number,
  bills: EntryModel[],
  expenses: EntryModel[],
  cards: ComputedLine[],
  cardItems: CardItemModel[],
  statements: CardStatementSummary[],
  totals: { incomes: number, bills: number, expenses: number, balance: number }
}

// Values that come from other screens: dividends (Investments) and credit card statements
@Injectable({
  providedIn: 'root'
})
export class FinanceSummaryService {

  private store = inject(FinanceStoreService);

  // Dividends registered in Investments with payment date in the month
  async dividends(month: Month): Promise<number> {
    const docs = await getDocs(ownedCollection('Dividends'));
    return round2(docs.docs
      .map(d => d.data())
      .filter(d => d['date'] && monthOf(toDate(d['date'])) == month)
      .reduce((sum, d) => sum + (Number(d['total']) || 0), 0));
  }

  // One line per card with the sum of its statement items
  async cardLines(month: Month, items?: CardItemModel[]): Promise<ComputedLine[]> {
    const [cards, cardItems] = await Promise.all([
      this.store.list<CardModel>('Cards'),
      items ? Promise.resolve(items) : this.store.list<CardItemModel>('CardItems', month)
    ]);

    return cards
      .map(card => ({
        description: `Cartão ${card.name}`,
        category: 'Cartão de Crédito',
        value: round2(cardItems.filter(i => i.card == card.id).reduce((sum, i) => sum + (Number(i.value) || 0), 0))
      }))
      .filter(line => line.value != 0);
  }

  // Everything the dashboards need, read once; months are computed with summarizeFinanceMonth
  async load(): Promise<FinanceRecords> {
    const [incomes, bills, expenses, cardItems, cards, statements, categories, dividends] = await Promise.all([
      this.store.list<EntryModel>('Incomes'),
      this.store.list<EntryModel>('Bills'),
      this.store.list<EntryModel>('Expenses'),
      this.store.list<CardItemModel>('CardItems'),
      this.store.list<CardModel>('Cards'),
      this.store.list<CardStatementModel>('CardStatements'),
      this.store.list<CategoryModel>('FinanceCategories'),
      getDocs(ownedCollection('Dividends')).then(docs => docs.docs
        .map(d => d.data())
        .filter(d => d['date'])
        .map(d => ({ date: toDate(d['date']), total: Number(d['total']) || 0 })))
    ]);
    return { incomes, bills, expenses, cardItems, cards, statements, categories, dividends };
  }

  async month(month: Month): Promise<MonthSummary> {
    return summarizeFinanceMonth(await this.load(), month);
  }
}

export interface FinanceRecords {
  incomes: EntryModel[],
  bills: EntryModel[],
  expenses: EntryModel[],
  cardItems: CardItemModel[],
  cards: CardModel[],
  statements: CardStatementModel[],
  categories: CategoryModel[],
  dividends: { date: Date, total: number }[]
}

export function summarizeFinanceMonth(records: FinanceRecords, month: Month): MonthSummary {
  const ofMonth = <T extends { month: Month }>(items: T[]) => items.filter(i => i.month == month);
  const incomes = ofMonth(records.incomes);
  const bills = ofMonth(records.bills);
  const expenses = ofMonth(records.expenses);
  const cardItems = ofMonth(records.cardItems);
  const dividends = round2(records.dividends.filter(d => monthOf(d.date) == month).reduce((total, d) => total + d.total, 0));

  const statements = records.cards
    .map(card => {
      const items = sortByCategory(cardItems.filter(i => i.card == card.id));
      return { card, items, total: sum(items) };
    })
    .filter(s => s.items.length)
    .sort((a, b) => a.card.name.localeCompare(b.card.name));
  const cards = statements
    .map(s => ({ description: `Cartão ${s.card.name}`, category: 'Cartão de Crédito', value: s.total }))
    .filter(line => line.value != 0);

  const totalIncomes = round2(sum(incomes) + dividends);
  const totalBills = round2(sum(bills));
  const totalExpenses = round2(sum(expenses) + sum(cards));

  return {
    incomes, dividends, bills, expenses, cards, cardItems, statements,
    totals: {
      incomes: totalIncomes,
      bills: totalBills,
      expenses: totalExpenses,
      balance: round2(totalIncomes - totalBills - totalExpenses)
    }
  };
}

// Largest categories first and, inside each one, largest values first
function sortByCategory(items: CardItemModel[]): CardItemModel[] {
  const totals = new Map<string, number>();
  for (const item of items)
    totals.set(item.category, (totals.get(item.category) ?? 0) + (Number(item.value) || 0));

  return [...items].sort((a, b) =>
    (totals.get(b.category)! - totals.get(a.category)!)
    || a.category.localeCompare(b.category)
    || b.value - a.value);
}

export function sum(lines: { value: number }[]): number {
  return round2(lines.reduce((total, l) => total + (Number(l.value) || 0), 0));
}

export function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

export function toDate(value: any): Date {
  if (value?.toDate) return value.toDate();
  if (value?.seconds != undefined) return new Date(value.seconds * 1000);
  return new Date(value);
}
